
import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAuth } from '@/components/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  LayoutDashboard, 
  Users, 
  DollarSign, 
  CreditCard, 
  Settings, 
  UserCheck,
  LogOut,
  ArrowLeft
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ThemeProvider } from '@/components/ThemeProvider';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import AdminLogin from './AdminLogin';

const AdminLayout = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
      toast.success('Sessão encerrada com sucesso');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Erro ao encerrar sessão');
    }
  };

  const navigationItems = [
    { path: '/adm', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/adm/users', icon: Users, label: 'Utilizadores' },
    { path: '/adm/revenue', icon: DollarSign, label: 'Rentabilidade' },
    { path: '/adm/subscriptions', icon: CreditCard, label: 'Subscrições' },
    { path: '/adm/plans', icon: Settings, label: 'Planos' },
    { path: '/adm/roles', icon: UserCheck, label: 'Administradores' },
  ];

  // Mostrar loading enquanto verifica autenticação
  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Se não está autenticado, mostrar tela de login
  if (!user) {
    return <AdminLogin />;
  }

  // Se está autenticado mas não é admin, mostrar acesso negado
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">Acesso Negado</h1>
          <p className="text-muted-foreground mb-6">
            Não tem permissões para aceder à área administrativa.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSignOut} 
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Encerrar Sessão
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Se é admin autenticado, mostrar interface administrativa
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background">
        {/* Mobile Sidebar Overlay */}
        <div className="md:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" style={{ display: 'none' }} id="mobile-sidebar-overlay">
          <div className="fixed inset-y-0 left-0 w-80 bg-card shadow-lg border-r border-border">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="p-4 border-b border-border flex justify-between items-center">
                <h1 className="text-lg font-bold text-foreground">
                  Admin
                </h1>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    const overlay = document.getElementById('mobile-sidebar-overlay');
                    if (overlay) overlay.style.display = 'none';
                  }}
                >
                  ✕
                </Button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-4 space-y-2">
                {navigationItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.path}
                      variant={isActive ? "default" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => {
                        navigate(item.path);
                        const overlay = document.getElementById('mobile-sidebar-overlay');
                        if (overlay) overlay.style.display = 'none';
                      }}
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.label}
                    </Button>
                  );
                })}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t border-border space-y-2">
                <ThemeToggle />
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleSignOut}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sair
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop/Tablet Sidebar - Always visible from md breakpoint up */}
        <div className="hidden md:block fixed inset-y-0 left-0 w-64 bg-card shadow-lg border-r border-border">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h1 className="text-xl font-bold text-foreground">
                Área Administrativa
              </h1>
              <ThemeToggle />
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate(item.path)}
                  >
                    <item.icon className="w-4 h-4 mr-3" />
                    {item.label}
                  </Button>
                );
              })}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-border">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleSignOut}
              >
                <LogOut className="w-4 h-4 mr-3" />
                Sair
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Header - Only visible on mobile */}
        <div className="md:hidden bg-card border-b border-border p-4 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const overlay = document.getElementById('mobile-sidebar-overlay');
              if (overlay) overlay.style.display = 'block';
            }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </Button>
          <h1 className="text-lg font-bold text-foreground">Admin</h1>
          <ThemeToggle />
        </div>

        {/* Main Content */}
        <div className="md:ml-64">
          <main className="p-4 md:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default AdminLayout;
